import React, { ChangeEvent, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import {
  MdmDeviceDto,
  MdmObject,
  useDeleteDeviceMutation,
  useGetObjectsByProjectQuery,
  useGetProjectQuery,
} from "gql/generated/types";
import { Button } from "primereact/button";
import { Chips, ChipsChangeParams } from "primereact/chips";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { Dropdown, DropdownChangeParams } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { TabPanel, TabView } from "primereact/tabview";
import { Controller, ControllerRenderProps, useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { getFormErrorMessage } from "../utils/utils";
import AdditionalInfo from "./AdditionalInfo";
import DatasetsTab from "./DatasetsTab";
import DeviceStatusTag from "./DeviceStatus";
import StatusHistory from "./StatusHistory";

type DeviceFormProps = {
  device: Partial<MdmDeviceDto> | null;
  onSubmit: (device: Partial<MdmDeviceDto>) => void;
  newForm?: boolean;
  isDialog?: boolean;
  closeDialog?: () => void;
  loading?: boolean;
};

type DeviceFormType = { identifier: string; mdmObject: Partial<MdmObject> };

const defaultDevice: Partial<MdmDeviceDto> = {
  tags: [],
};

const schema = z.object({
  identifier: z.string().min(1, "Required"),
  mdmObject: z.object({
    identifier: z.string(),
    id: z.string(),
  }),
});

const DeviceForm: React.FC<DeviceFormProps> = ({
  device,
  onSubmit,
  newForm = false,
  isDialog = false,
  closeDialog,
  loading = false,
}) => {
  const [deviceState, setDeviceState] = useState<Partial<MdmDeviceDto>>(device ?? defaultDevice);
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<DeviceFormType>({
    resolver: zodResolver(schema),
  });

  const { projectId } = useParams();

  const { data: projectData } = useGetProjectQuery({ variables: { id: projectId! }, skip: !projectId });
  const deviceAttributes = projectData?.mdmProjectById?.configuration?.deviceConfiguration.customFields ?? [];
  const deviceTypes = projectData?.mdmProjectById?.configuration?.deviceConfiguration.measurementTypes ?? [];

  const { data: objectData } = useGetObjectsByProjectQuery({
    variables: { id: projectId! },
    skip: !projectId,
  });
  const [deleteDeviceMutation, { loading: loadingDelete }] = useDeleteDeviceMutation({
    onCompleted: () => {
      easasAlert({
        severity: "success",
        summary: "Success",
        detail: "Device deleted successfully",
        life: 3000,
      });
      navigate(-1);
    },
    onError: err => {
      easasAlert({ severity: "error", summary: "Error", detail: err.message, life: 3000 });
    },
    refetchQueries: ["GetObject"],
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement> | ChipsChangeParams) => {
    const { name, value } = e.target;
    setDeviceState(prev => ({ ...prev, [name]: value }));
  };

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    confirmPopup({
      target: e.currentTarget,
      message: "Are you sure you want to delete this device?",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => {
        deleteDeviceMutation({
          variables: {
            id: deviceState.id!,
          },
          update(cache) {
            const normalizedId = cache.identify({ id: deviceState.id, __typename: "MdmDeviceDTO" });
            cache.evict({ id: normalizedId });
            cache.gc();
          },
        });
      },
    });
  };

  const handleDeviceTypeChange = (e: DropdownChangeParams) => {
    setDeviceState(prev => ({ ...prev, objectRelation: { ...prev.objectRelation, roleInObject: e.value, roleIdentifier: ""} }));
  }

  const handleRoleIdentifierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDeviceState(prev => ({
      ...prev,
      objectRelation: {
        ...prev.objectRelation,
        roleIdentifier: e.target.value,
      },
    }));
  }

  const handleDeviceConfigChange = (e: DropdownChangeParams) => {
    const config = projectData?.mdmProjectById?.deviceConfigs?.find(
      cfg => cfg?.id === e.value
    );
    setDeviceState(prev => ({ ...prev, config }));
  }

  const renderIdentifierControl = (field: ControllerRenderProps<DeviceFormType, any>) => (
    <InputLabelWrapper labelName="Identifier" col={2} >
      <InputText
        value={field.value}
        onChange={e => {
          field.onChange(e.target.value);
          handleChange(e);
        }}
        className="p-inputtext-sm"
        name="identifier"
        placeholder="Identifier"
      />
      {getFormErrorMessage("identifier", errors)}
    </InputLabelWrapper>
  );

  const renderMdmObjectControl = (field: ControllerRenderProps<DeviceFormType, any>) => (
    <InputLabelWrapper labelName="Object" col={2} >
      <Dropdown
        value={field.value}
        options={objectData?.mdmObjectsByProjectId.map(object => ({
          identifier: object.identifier,
          id: object.id,
        }))}
        onChange={e => {
          field.onChange(e.value);
          setDeviceState(prev => ({ ...prev, objectRelation: { ...prev.objectRelation, mdmObject: e.value } }));
        }}
        optionLabel="identifier"
        name="mdmObject"
        placeholder="Object"
        className="p-inputtext-sm"
        filter
      />
      {errors.mdmObject?.identifier && (
        <small className="p-error">{errors.mdmObject.identifier.message}</small>
      )}
    </InputLabelWrapper>
  );

  return (
    <div id="device-form" className="mx-auto">
      <ConfirmPopup />
      <form className="flex justify-content-between flex-column gap-5">
        <div className="grid p-fluid">
          <Controller
            defaultValue={deviceState.identifier ?? ""}
            control={control}
            name="identifier"
            render={({ field }) => renderIdentifierControl(field)}
          />
          <Controller
            defaultValue={{
              identifier: deviceState.objectRelation?.mdmObject?.identifier!,
              id: deviceState.objectRelation?.mdmObject?.id,
            }}
            control={control}
            name="mdmObject"
            render={({ field }) => renderMdmObjectControl(field)}
          />
          <div className="text-left col-1 mt-5">
            {deviceState.objectRelation?.mdmObject?.id && (
              <Link
                to={`/mdm/${projectId}/objects/${deviceState.objectRelation?.mdmObject?.id}`}
                title="Object Info"
              >
                <i className="pi pi-external-link mt-4" />
              </Link>
            )}
          </div>
          <InputLabelWrapper labelName="Role" col={2}>
            <Dropdown
              value={deviceState.objectRelation?.roleInObject}
              options={deviceTypes ?? []}
              optionLabel="title"
              optionValue="id"
              className="p-inputtext-sm"
              onChange={handleDeviceTypeChange}
              showClear
            />
          </InputLabelWrapper>
          <InputLabelWrapper labelName="Role Identifier" col={2}>
            <InputText
              value={deviceState.objectRelation?.roleIdentifier ?? ""}
              placeholder="Role Identifier"
              className="p-inputtext-sm"
              onChange={handleRoleIdentifierChange}
            />
          </InputLabelWrapper>
          <InputLabelWrapper labelName="Configuration" col={2}>
            <Dropdown
              value={deviceState.config?.id}
              options={projectData?.mdmProjectById?.deviceConfigs ?? []}
              optionLabel="title"
              optionValue="id"
              className="p-inputtext-sm"
              onChange={handleDeviceConfigChange}
            />
          </InputLabelWrapper>
          <div className="col-1 mt-2 flex justify-content-end align-items-start gap-2">
            {device && (
              <DeviceStatusTag
                status={device.status?.status!}
                lastMeasurementDate={device.status?.lastMeasurementDate}
              />
            )}
          </div>
          <InputLabelWrapper labelName="Tags" col={12}>
            <Chips
              value={deviceState.tags ?? []}
              onChange={handleChange}
              className="p-inputtext-sm"
              name="tags"
              placeholder="Tags.."
            />
          </InputLabelWrapper>
          {!newForm && (
            <>
              <InputLabelWrapper labelName="Created at:" className="align-items-baseline" col={4} flexDirection="row">
                <InputText
                  value={new Date(deviceState.createdAt).toLocaleString() ?? ""}
                  className="border-none text-gray-900 w-15rem "
                  style={{ backgroundColor: "transparent" }}
                  name="createdAt"
                  disabled
                />
              </InputLabelWrapper>
              <InputLabelWrapper labelName="Updated at:" className="align-items-baseline" col={4} flexDirection="row">
                <InputText
                  value={new Date(deviceState.modifiedAt).toLocaleString() ?? ""}
                  className="border-none text-gray-900 w-15rem "
                  style={{ backgroundColor: "transparent" }}
                  name="modifiedAt"
                  disabled
                />
              </InputLabelWrapper>
            </>
          )}
          {deviceAttributes.length > 0 && (
            <AdditionalInfo
              attributes={deviceAttributes}
              objectState={deviceState}
              setObjectState={setDeviceState}
            />
          )}
        </div>
      </form>
      {device && !newForm && (
        <TabView>
          <TabPanel header="Datasets">
            <DatasetsTab datasets={device.boundDatasets?.all} />
          </TabPanel>
          <TabPanel header="Status History">
            <StatusHistory device={device} />
          </TabPanel>
        </TabView>
      )}
      <div className="flex w-full justify-content-end gap-3">
        <Button
          type="submit"
          onClick={handleSubmit(() => onSubmit(deviceState))}
          label={newForm ? "Create" : "Update"}
          className={`p-button-rounded p-button-${newForm ? "success" : "primary"} w-10rem`}
          loading={loading}
        />
        {isDialog && (
          <Button
            type="button"
            label="Close"
            onClick={closeDialog}
            className="p-button-rounded p-button-secondary p-button-outlined w-10rem"
          />
        )}
        {device && !newForm && (
          <>
            <Button
              type="button"
              label="Delete"
              className="p-button-rounded p-button-danger w-10rem"
              onClick={handleDelete}
              loading={loadingDelete}
            />
            <Button
              type="button"
              onClick={() => setDeviceState(device ?? {})}
              label="Cancel"
              className="p-button-rounded p-button-secondary p-button-outlined w-10rem"
            />
          </>
        )}
        {newForm && !device && !isDialog && (
          <Button
            type="button"
            onClick={() => navigate(-1)}
            label="Back"
            className="p-button-rounded p-button-secondary p-button-outlined w-10rem"
          />
        )}
      </div>
    </div>
  );
};

export default DeviceForm;
